export const UNAUTHORIZED_CLOSE_CODES = new Set([4001, 4003]);

export const normalizeWebSocketBase = (baseUrl) => {
  if (!baseUrl) return null;
  if (baseUrl.startsWith('ws://') || baseUrl.startsWith('wss://')) {
    return baseUrl.replace(/\/+$/, '');
  }
  if (baseUrl.startsWith('http://') || baseUrl.startsWith('https://')) {
    const parsed = new URL(baseUrl);
    const wsProtocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${parsed.host}`;
  }
  return null;
};

export const resolveWebSocketUrl = (
  path = '/ws',
  locationObj = window.location,
  env = process.env,
) => {
  const envBase = normalizeWebSocketBase(
    env?.REACT_APP_WS_URL || env?.REACT_APP_API_URL || null,
  );
  if (envBase) {
    return `${envBase}${path}`;
  }
  const protocol = locationObj?.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = locationObj?.host || '';
  return `${protocol}//${host}${path}`;
};

export const createWebSocketAdapter = ({
  path = '/ws',
  reconnect = true,
  reconnectInterval = 5000,
  maxReconnectAttempts = 5,
  tokenProvider,
  WebSocketImpl = WebSocket,
  locationObj = window.location,
  env = process.env,
  onOpen,
  onMessage,
  onError,
  onClose,
  onUnauthorizedClose,
}) => {
  let socket = null;
  let reconnectAttempts = 0;
  let reconnectTimer = null;
  let manuallyClosed = false;

  const clearReconnectTimer = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const scheduleReconnect = () => {
    if (!reconnect || manuallyClosed || reconnectAttempts >= maxReconnectAttempts) {
      return;
    }
    reconnectAttempts += 1;
    reconnectTimer = setTimeout(() => {
      connect().catch((error) => {
        if (onError) onError(error);
      });
    }, reconnectInterval);
  };

  const connect = async () => {
    clearReconnectTimer();
    if (typeof tokenProvider !== 'function') {
      throw new Error('tokenProvider is required for WebSocket auth handshake');
    }
    const token = await tokenProvider();
    if (!token) {
      throw new Error('WebSocket token is required');
    }
    const url = resolveWebSocketUrl(path, locationObj, env);
    socket = new WebSocketImpl(url);

    socket.onopen = () => {
      reconnectAttempts = 0;
      socket.send(JSON.stringify({ type: 'auth', token }));
      if (onOpen) onOpen();
    };

    socket.onmessage = (event) => {
      if (onMessage) onMessage(event);
    };

    socket.onerror = (event) => {
      if (onError) onError(event);
    };

    socket.onclose = (event) => {
      if (UNAUTHORIZED_CLOSE_CODES.has(event.code) && onUnauthorizedClose) {
        onUnauthorizedClose(event);
      }
      if (onClose) onClose(event);
      scheduleReconnect();
    };

    return socket;
  };

  const close = () => {
    manuallyClosed = true;
    clearReconnectTimer();
    if (socket) {
      socket.close();
    }
  };

  const send = (payload) => {
    if (!socket || socket.readyState !== WebSocketImpl.OPEN) {
      return false;
    }
    socket.send(JSON.stringify(payload));
    return true;
  };

  const getSocket = () => socket;

  return {
    connect,
    close,
    send,
    getSocket,
  };
};

export const openAuthenticatedWebSocket = async (options = {}) => {
  const adapter = createWebSocketAdapter({ ...options, reconnect: false });
  return adapter.connect();
};
